import { Icon } from '@components/primitives/Icon'
import { faPause } from '@fortawesome/pro-solid-svg-icons/faPause'
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { type TrackingSources } from '@lib/tracking/TrackingSources'
import { type MediaHTMLAttributes, useEffect, useRef, useState } from 'react'

export const NativeAudioPlayer = ({
  audioRef,
  preload = 'metadata',
  src,
  trackingSource,
}: {
  audioRef?: React.RefObject<HTMLAudioElement>
  preload?: MediaHTMLAttributes<HTMLAudioElement>['preload']
  src: string
  trackingSource?: TrackingSources
}) => {
  const fallbackAudioRef = useRef<HTMLAudioElement>(null)
  const effectiveAudioRef = audioRef ?? fallbackAudioRef

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const calculateTime = (secs: number) => {
    if (isNaN(secs)) return '0:00'
    const minutes = Math.floor(secs / 60)
    const seconds = Math.floor(secs % 60)
    const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`
    return `${minutes}:${returnedSeconds}`
  }

  useEffect(() => {
    const audio = effectiveAudioRef.current
    if (!audio) return

    const setAudioData = () => {
      if (!isNaN(audio.duration)) {
        setDuration(audio.duration)
      }
    }

    const setAudioTime = () => {
      if (!isNaN(audio.currentTime)) {
        setCurrentTime(audio.currentTime)
        if (audio.paused) {
          // handle case when pat-nav modal is closed and re-opened
          setIsPlaying(false)
        }
      }
    }

    // Add event listeners
    audio.addEventListener('loadedmetadata', setAudioData)
    audio.addEventListener('timeupdate', setAudioTime)
    audio.addEventListener('durationchange', setAudioData)

    // Cleanup
    return () => {
      audio.removeEventListener('loadedmetadata', setAudioData)
      audio.removeEventListener('timeupdate', setAudioTime)
      audio.removeEventListener('durationchange', setAudioData)
    }
  }, [])

  const togglePlayPause = () => {
    const audio = effectiveAudioRef.current
    if (!audio) return

    if (isPlaying) {
      trackEvent(TrackingEvents.INBOX_PAT_NAV_AGENT_PAUSE_CLICKED, {
        source: trackingSource,
      })
      audio.pause()
    } else {
      trackEvent(TrackingEvents.INBOX_PAT_NAV_AGENT_PLAY_CLICKED, {
        source: trackingSource,
      })
      audio.play()
    }
    setIsPlaying(!isPlaying)
  }

  return (
    <div>
      <div className='flex flex-row items-center'>
        <div
          className={`mr-2 size-2 rounded-full ${isPlaying ? 'animate-pulse bg-destructive500' : 'bg-transparent'}`}
        />
        <div className='mr-3 w-16 text-center text-2xl font-semibold'>
          {calculateTime(duration - currentTime)}
        </div>
        <button
          aria-label={isPlaying ? 'Pause' : 'Play'}
          className='flex size-8 cursor-pointer items-center justify-center rounded-full bg-blue500'
          onClick={togglePlayPause}
          tabIndex={0}
        >
          <Icon
            className='bg-blue500 text-white'
            icon={isPlaying ? faPause : faPlay}
          />
        </button>
      </div>
      <audio preload={preload} ref={effectiveAudioRef} src={src} />
    </div>
  )
}
